import { http } from 'cdui-js';
import { composeSignatureOnAttachment } from './open-account';

export type StockTransferMarketCode = 'HK' | 'US';

export type StockTransferInSettlement = {
  companyName?: string;
  ccassId?: string;
  dtcNo?: string;
  dtcName?: string;
  accountNo?: string;
  accountName?: string;
  contactPerson?: string;
  contact?: string;
  email?: string;
  settlementAccountHint?: string;
  status?: number;
};

export type StockTransferOutBroker = {
  id: number;
  nameEn?: string;
  nameSc?: string;
  nameTc?: string;
  logo?: string;
  brokerCode?: string;
  accountNo?: string;
  accountName?: string;
  contactPerson?: string;
  contactPhone?: string;
  /** 1 = 其他 */
  isOther?: number;
  sortOrder?: number;
  status?: number;
};

const unwrap = <T>(res: unknown): T => {
  if (res && typeof res === 'object' && 'data' in res && 'code' in res) {
    return (res as { data: T }).data;
  }
  return res as T;
};

/** `GET /user/stockTransferIn/settlement/{marketCode}` */
export const fetchStockTransferInSettlement = async (marketCode: string) => {
  const res = await http.get(`/user/stockTransferIn/settlement/${encodeURIComponent(marketCode)}`);
  return unwrap<StockTransferInSettlement>(res) ?? {};
};

/** `GET /user/stockTransferIn/outBroker/list` */
export const fetchStockTransferOutBrokers = async (keyword?: string) => {
  const query = keyword ? `?keyword=${encodeURIComponent(keyword)}` : '';
  const res = await http.get(`/user/stockTransferIn/outBroker/list${query}`);
  const data = unwrap<StockTransferOutBroker[] | { list?: StockTransferOutBroker[] }>(res);
  if (Array.isArray(data)) return data;
  return data?.list ?? [];
};

export type StockTransferInStockItem = {
  stockCode: string;
  stockName?: string;
  marketId: string;
  quantity: number;
};

export type StockTransferInStockItemResp = {
  id?: number;
  stockCode?: string;
  stockName?: string;
  marketId?: string;
  quantity?: number;
};

/** `POST /user/stockTransferIn/apply/save`（草稿，不启动工作流） */
export type StockTransferInApplySaveBody = {
  /** 申请 ID（int64，须字符串避免 JS 精度丢失） */
  id?: string;
  marketCode?: string;
  outBrokerId?: number;
  outBrokerCustomName?: string;
  outBrokerAccountNo?: string;
  outBrokerAccountName?: string;
  outBrokerContactPerson?: string;
  outBrokerContactPhone?: string;
  inBrokerName?: string;
  inCcassId?: string;
  inAccountType?: string;
  inAccountNum?: string;
  statementFilePaths?: string;
  marketExtraJson?: string;
};

/** `POST /user/stockTransferIn/apply/submit`：有 id 仅启动工作流；无 id 则一次性落库并提交 */
export type StockTransferInApplySubmitBody = {
  id?: string;
  marketCode?: string;
  outBrokerId?: number;
  outBrokerCustomName?: string;
  outBrokerAccountNo?: string;
  outBrokerAccountName?: string;
  outBrokerContactPerson?: string;
  outBrokerContactPhone?: string;
  inBrokerName?: string;
  inCcassId?: string;
  inAccountType?: string;
  inAccountNum?: string;
  statementFilePaths?: string;
  marketExtraJson?: string;
  stocks?: StockTransferInStockItem[];
};

export type StockTransferInApplyResp = {
  id?: number | string;
  applyNo?: string;
  status?: string;
};

const withStringApplyId = <T extends { id?: string }>(body: T): T => {
  if (body.id == null || body.id === '') return body;
  return { ...body, id: String(body.id).trim() };
};

export const saveStockTransferInApply = async (body: StockTransferInApplySaveBody) => {
  const res = await http.post('/user/stockTransferIn/apply/save', withStringApplyId(body));
  return unwrap<StockTransferInApplyResp>(res) ?? {};
};

export const submitStockTransferInApply = async (body: StockTransferInApplySubmitBody) => {
  const res = await http.post('/user/stockTransferIn/apply/submit', withStringApplyId(body));
  return unwrap<StockTransferInApplyResp>(res) ?? {};
};

export type StockTransferInStockBatchSaveBody = {
  /** 申请 ID（int64，须字符串提交） */
  applyId: string;
  stocks: StockTransferInStockItem[];
};

/** 全量覆盖该申请下股票明细 */
export const batchSaveStockTransferInStocks = async (body: StockTransferInStockBatchSaveBody) => {
  const res = await http.post('/user/stockTransferIn/stock/batchSave', {
    ...body,
    applyId: String(body.applyId).trim(),
  });
  return unwrap<StockTransferInStockItemResp[]>(res) ?? [];
};

export type StockTransferInStockSaveBody = {
  id?: number;
  applyId: string;
  stockCode: string;
  stockName?: string;
  marketId: string;
  quantity: number;
};

export const saveStockTransferInStock = async (body: StockTransferInStockSaveBody) => {
  const res = await http.post('/user/stockTransferIn/stock/save', body);
  return unwrap<StockTransferInStockItemResp>(res) ?? {};
};

/** 转入交收指示 PDF 贴签（与开户 composeSignatureOnAttachment 相同，attachType 不同） */
export const composeStockTransferSettlementSignature = (applyId: string, signFile: File | Blob) =>
  composeSignatureOnAttachment(
    applyId,
    signFile,
    'stock_transfer_in_settlement_instruction_signed',
  );

export type StockTransferInWfStatus =
  | 'SUBMITTED'
  | 'APPROVING'
  | 'BACK'
  | 'REFUSED'
  | 'FINISHED'
  | 'CANCELLED'
  | 'STOP'
  | string;

export type StockTransferInApplyStockItem = {
  id?: number;
  stockCode?: string;
  stockName?: string;
  marketId?: string;
  quantity?: number;
};

/** 转入申请列表项 / 详情（与 `UserStockTransferInApplyRespDTO` 对齐） */
export type StockTransferInApplyRecord = {
  id?: number | string;
  applyNo?: string;
  marketCode?: string;
  outBrokerId?: number;
  outBrokerNameEn?: string;
  outBrokerNameSc?: string;
  outBrokerNameTc?: string;
  outBrokerIsOther?: number;
  outBrokerAccountNo?: string;
  outBrokerAccountName?: string;
  outBrokerContactPerson?: string;
  outBrokerContactPhone?: string;
  inBrokerName?: string;
  inCcassId?: string;
  inAccountType?: string;
  inAccountNum?: string;
  marketExtraJson?: string;
  status?: StockTransferInWfStatus;
  /** 申请时间：毫秒/秒时间戳或日期字符串 */
  createTime?: string | number;
  updateTime?: string | number;
  stocks?: StockTransferInApplyStockItem[];
};

export type StockTransferInApplyPageQuery = {
  keyword?: string;
  applyNo?: string;
  marketCode?: string;
  status?: string;
  beginTime?: string;
  endTime?: string;
  pageNum?: number;
  pageSize?: number;
  order?: string;
  asc?: boolean;
};

export type StockTransferInApplyPageResult = {
  total: number;
  list: StockTransferInApplyRecord[];
};

/** `GET /user/stockTransferIn/apply/page` — 转入记录分页 */
export const fetchStockTransferInApplyPage = async (
  params: StockTransferInApplyPageQuery = {},
): Promise<StockTransferInApplyPageResult> => {
  const query = new URLSearchParams();
  query.set('pageNum', String(params.pageNum ?? 1));
  query.set('pageSize', String(params.pageSize ?? 20));
  if (params.keyword?.trim()) query.set('keyword', params.keyword.trim());
  if (params.applyNo?.trim()) query.set('applyNo', params.applyNo.trim());
  if (params.marketCode?.trim()) query.set('marketCode', params.marketCode.trim());
  if (params.status?.trim()) query.set('status', params.status.trim());
  if (params.beginTime?.trim()) query.set('beginTime', params.beginTime.trim());
  if (params.endTime?.trim()) query.set('endTime', params.endTime.trim());
  if (params.order?.trim()) query.set('order', params.order.trim());
  if (params.asc != null) query.set('asc', String(params.asc));

  const res = await http.get(`/user/stockTransferIn/apply/page?${query.toString()}`);
  const data = unwrap<{ total?: number; list?: StockTransferInApplyRecord[] }>(res);
  return {
    total: Number(data?.total) || 0,
    list: Array.isArray(data?.list) ? data.list : [],
  };
};

/** `GET /user/stockTransferIn/apply/{id}` — 转入记录详情 */
export const fetchStockTransferInApplyDetail = async (applyId: string): Promise<StockTransferInApplyRecord> => {
  const id = String(applyId || '').trim();
  if (!id) throw new Error('applyId required');
  const res = await http.get(`/user/stockTransferIn/apply/${encodeURIComponent(id)}`);
  return unwrap<StockTransferInApplyRecord>(res) ?? {};
};

/** `POST /user/stockTransferIn/apply/{id}/cancel` — 取消申请（DRAFT / SUBMITTED） */
export const cancelStockTransferInApply = async (applyId: string, cancelReason?: string) => {
  const id = String(applyId || '').trim();
  if (!id) throw new Error('applyId required');
  const query = cancelReason?.trim()
    ? `?cancelReason=${encodeURIComponent(cancelReason.trim())}`
    : '';
  const res = await http.post(
    `/user/stockTransferIn/apply/${encodeURIComponent(id)}/cancel${query}`,
    {},
  );
  return unwrap<StockTransferInApplyResp>(res) ?? {};
};
